export enum StoryCategory {
  Industry = 'Industry',
  Size = 'Size',
  UseCase = 'UseCase',
}

export enum IndustryCategory {
  Technology = 'Technology',
  Automotive = 'Automotive',
  Financial = 'Financial',
}

export enum SizeCategory {
  Enterprises = 'Enterprises',
  Teams = 'Teams',
  Startups = 'Startups',
}

export enum UseCaseCategory {
  DevSecOps = 'DevSecOps',
  Automation = 'Automation',
  DevOps = 'DevOps',
}

export type StorySubCategory = IndustryCategory | SizeCategory | UseCaseCategory

interface CustomerStory {
  category: StoryCategory
  subCategory: StorySubCategory
  description: string
  url: string
  logo: {
    url: string
    alt: string
    height?: string
  }
}

type CustomerStoryData = Omit<CustomerStory, 'category' | 'subCategory' | 'logo' | 'url'> & {
  logo: Omit<CustomerStory['logo'], 'url'>
}

type SubCategoryDictionaryData = Record<string, CustomerStoryData>
type SubCategoryDictionary = Record<string, CustomerStory>
type CustomerStoryDictionary = Record<StoryCategory, Partial<Record<StorySubCategory, SubCategoryDictionary>>>
type CustomerStoryDataDictionary = Record<StoryCategory, Partial<Record<StorySubCategory, SubCategoryDictionaryData>>>

const technologyStories: SubCategoryDictionaryData = {
  duolingo: {
    description: 'Duolingo boosts developer speed by 25% with Copilot',
    logo: {
      // TODO: Update once known
      alt: '',
      height: '56px',
    },
  },
}

const automotiveStories: SubCategoryDictionaryData = {
  mercedesBenz: {
    description: 'Mercedez-Benz standardizes source code and automates onboarding',
    logo: {
      // TODO: Update once known
      alt: '',
      height: '48px',
    },
  },
}

const financialStories: SubCategoryDictionaryData = {
  mercadoLibre: {
    description: 'Mercado Libre cuts coding time by 50%',
    logo: {
      // TODO: Update once known
      alt: '',
      height: '40px',
    },
  },
}

const enterprisesStories: SubCategoryDictionaryData = {
  accenture: {
    description: 'Accenture customizes Copilot for its 12,000 developers',
    logo: {
      // TODO: Update once known
      alt: '',
      height: '36px',
    },
  },
  philips: {
    description: 'See how Philips reduced infrastructure costs by 80%',
    logo: {
      // TODO: Update once known
      alt: '',
      height: '34px',
    },
  },
}

const teamsStories: SubCategoryDictionaryData = {
  buffer: {
    description: 'Buffer enhances collaboration and control with GitHub Team',
    logo: {
      // TODO: Update once known
      alt: '',
      height: '32px',
    },
  },
}

const devSecOpsStories: SubCategoryDictionaryData = {
  telus: {
    description: 'Learn how Telus saved $16.9M with GitHub',
    logo: {
      // TODO: Update once known
      alt: '',
      height: '36px',
    },
  },
}

const automationStories: SubCategoryDictionaryData = {
  fullstory: {
    description: 'Fullstory automates DevSecOps at scale with GitHub',
    logo: {
      // TODO: Update once known
      alt: '',
      height: '36px',
    },
  },
}

const devOpsStories: SubCategoryDictionaryData = {
  ey: {
    description: 'EY leverages GitHub and Microsoft Azure DevOps to outpace the competition',
    logo: {
      // TODO: Update once known
      alt: '',
      height: '52px',
    },
  },
}

const customerStoriesData: CustomerStoryDataDictionary = {
  [StoryCategory.Industry]: {
    [IndustryCategory.Technology]: technologyStories,
    [IndustryCategory.Automotive]: automotiveStories,
    [IndustryCategory.Financial]: financialStories,
  },
  [StoryCategory.Size]: {
    [SizeCategory.Enterprises]: enterprisesStories,
    [SizeCategory.Teams]: teamsStories,
  },
  [StoryCategory.UseCase]: {
    [UseCaseCategory.DevSecOps]: devSecOpsStories,
    [UseCaseCategory.Automation]: automationStories,
    [UseCaseCategory.DevOps]: devOpsStories,
  },
}

// TODO: Update logo assets once known
const LOGO_PATH = '/images/modules/site/home24/customer-stories/logos'
const BASE_URL = 'https://github.com/customer-stories'

const toKebabCase = (string: string) => {
  return string
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Insert dash between lowercase and uppercase letters
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2') // Handle cases like 'XMLHttpRequest'
    .toLowerCase() // Convert all to lowercase
}

export const CUSTOMER_STORIES = {} as CustomerStoryDictionary

// eslint-disable-next-line github/array-foreach
Object.entries(customerStoriesData).forEach(([category, subCategories]) => {
  const typedCategory = category as StoryCategory
  if (!CUSTOMER_STORIES[typedCategory]) CUSTOMER_STORIES[typedCategory] = {}

  // eslint-disable-next-line github/array-foreach
  Object.entries(subCategories).forEach(([subCategory, stories]) => {
    const typedSubCategory = subCategory as StorySubCategory

    if (!CUSTOMER_STORIES[typedCategory][typedSubCategory]) CUSTOMER_STORIES[typedCategory][typedSubCategory] = {}

    // eslint-disable-next-line github/array-foreach
    Object.entries(stories).forEach(([customerId, story]) => {
      const parsedCustomerId = toKebabCase(customerId)

      // @ts-expect-error The object is correctly initialized above
      CUSTOMER_STORIES[typedCategory][typedSubCategory][customerId] = {
        ...story,
        category: typedCategory,
        subCategory: typedSubCategory,
        url: `${BASE_URL}/${parsedCustomerId}`,
        logo: {
          ...story.logo,
          url: `${LOGO_PATH}/${parsedCustomerId}.svg`,
        },
      }
    })
  })
})

export const COPY = {
  intro: {
    title:
      'From startups to enterprises, <span>GitHub scales <br class="lp-breakText" /> with teams of any size in any industry.</span>',
  },
  content: {
    categories: {
      [StoryCategory.Industry]: 'By industry',
      [StoryCategory.Size]: 'By size',
      [StoryCategory.UseCase]: 'By use case',
    },
    subCategories: {
      [IndustryCategory.Technology]: 'Technology',
      [IndustryCategory.Automotive]: 'Automotive',
      [IndustryCategory.Financial]: 'Financial services',
      [SizeCategory.Enterprises]: 'Enterprises',
      [SizeCategory.Teams]: 'Teams',
      [SizeCategory.Startups]: 'Startups',
      [UseCaseCategory.DevSecOps]: 'DevSecOps',
      [UseCaseCategory.Automation]: 'Automation',
      [UseCaseCategory.DevOps]: 'DevOps',
    },
    readMore: 'Read customer story',
    aria: {
      // TODO: Update once known
      toggle: '',
    },
  },
  links: [
    {
      url: '/customer-stories',
      label: 'Explore customer stories',
    },
    {
      url: '/solutions',
      label: 'View all solutions',
    },
  ],
}
